import { ST } from '../state.js';
import { kwMatch, TYPE_KW } from './helpers.js';

// ── STOCK IMAGE SEARCH ────────────────────────────────────────────
// Search Unsplash, score-based assignment to sections.

// Pure API logic — no DOM dependency. Direct migration to composable.

/**
 * Search stock images and assign them to sections by relevance score.
 * @param {import('../types.ts').Concept} concept
 * @returns {Promise<void>}
 */
async function searchStockImages(concept) {
  const sections = concept.sections || [];

  // Build section metadata for client-side image assignment
  const sectionMeta = [];
  for (const s of sections) {
    if (['header', 'footer', 'trust-ticker', 'faq', 'stats', 'pricing', 'cta'].includes(s.type)) {
      continue;
    }
    sectionMeta.push({ sectionId: s.id, sectionType: s.type, title: s.title || s.type });
  }

  // Use AI-generated search queries from concept, or fall back to section titles
  const aiQueries = concept.imageSearchQueries || [];
  let query;
  if (aiQueries.length > 0) {
    query = aiQueries.join(' ').slice(0, 80);
  } else {
    const sectionTitles = sectionMeta
      .slice(0, 4)
      .map(s => s.title)
      .join(' ');
    query = (sectionTitles || 'professional business office team').slice(0, 80);
  }
  const allImages = [];
  try {
    const res = await fetch('/api/stock-images', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, per_page: 30 }),
    });
    const data = await res.json();
    const images = data.images || [];

    // Score-based image assignment using section-type semantic keywords
    const scoreImage = (img, sec) => {
      const alt = (img.alt || '').toLowerCase();
      if (!alt) {
        return 0;
      }
      let score = 0;
      // Section-type semantic keywords (strongest signal)
      const typeKws = TYPE_KW[sec.sectionType] || [];
      for (const kw of typeKws) {
        if (kwMatch(alt, kw)) {
          score += 3;
        }
      }
      // Title keywords (secondary signal)
      const titleKws = sec.title
        .toLowerCase()
        .split(/\s+/)
        .filter(w => w.length > 3);
      for (const kw of titleKws) {
        if (kwMatch(alt, kw)) {
          score += 2;
        }
      }
      return score;
    };

    // Assign best-scoring image to each section
    const assigned = new Set();
    for (const sec of sectionMeta) {
      let bestImg = null;
      let bestScore = 0;
      for (const img of images) {
        if (assigned.has(img.id)) {
          continue;
        }
        const s = scoreImage(img, sec);
        if (s > bestScore) {
          bestScore = s;
          bestImg = img;
        }
      }
      if (bestImg) {
        assigned.add(bestImg.id);
        allImages.push({ ...bestImg, forSection: sec.sectionId, forType: sec.sectionType });
      }
    }

    // Fallback: score remaining images against unmatched sections
    const unassignedSections = sectionMeta.filter(
      s => !allImages.some(img => img.forSection === s.sectionId),
    );
    const remaining = images.filter(img => !assigned.has(img.id));
    for (const sec of unassignedSections) {
      if (!remaining.length) {
        break;
      }
      let bestIdx = 0;
      let bestScore = -1;
      for (let i = 0; i < remaining.length; i++) {
        const s = scoreImage(remaining[i], sec);
        if (s > bestScore) {
          bestScore = s;
          bestIdx = i;
        }
      }
      const [pick] = remaining.splice(bestIdx, 1);
      allImages.push({ ...pick, forSection: sec.sectionId, forType: sec.sectionType });
    }

    // Rest goes to general pool
    for (const img of remaining) {
      allImages.push({ ...img, forSection: '_general', forType: 'general' });
    }
  } catch (e) {
    console.warn('Stock image search failed:', e);
  }

  // Deduplicate and store
  const seen = new Set();
  ST.stockImages = allImages.filter(img => {
    if (seen.has(img.id)) {
      return false;
    }
    seen.add(img.id);
    return true;
  });
}

// ── MODULE EXPORTS ──────
export { searchStockImages };
